import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertFormInitializationFragment from '@app/site/secured/rateshopper/rates/alert/fragment/AlertFormInitializationFragment';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { useForm } from 'react-hook-form';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import FormMultiSelect from '@app/component/form/input/select/FormMultiSelect';
import FormSelect from '@app/component/form/input/select/FormSelect';
import FormInputNumber from '@app/component/form/input/text/FormInputNumber';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import AlertFilterRoomTypesField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterRoomTypesField';
import AlertFilterRefundingField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterRefundingField';
import AlertFilterLengthOfStayField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterLengthOfStayField';
import AlertScheduleField from '@app/site/secured/rateshopper/rates/alert/fields/AlertScheduleField';
import AlertTimeFrameField from '@app/site/secured/rateshopper/rates/alert/fields/AlertTimeFrameField';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormButton from '@app/component/form/button/FormButton';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _has from 'lodash/has';
import Range from '@app/model/_common/Range';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import PriceComparisonAlertRuleTrigger from '@app/model/alert/PriceComparisonAlertRuleTrigger';
import PriceComparisonAlertRule from '@app/model/alert/PriceComparisonAlertRule';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { AlertRecipientsFragment } from '@app/site/secured/rateshopper/rates/alert/fragment/AlertRecipientsFragment';
import { AlertRecipientsType } from '@app/model/alert/AlertRecipientsType';
import CompetitorType from '@app/model/company_hotel/CompetitorType';
import AlertAddExtraEmailsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertAddExtraEmailsField';
import AlertFilterMealsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterMealsField';


const PriceComparisonAlertPage: React.FunctionComponent<{
	alertId: number | undefined,
	isCopy?: boolean
}> = ({ alertId, isCopy = false }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {

		return <AlertFormInitializationFragment alertId={alertId}
																						alertType={AlertType.PRICE_COMPARISON}
																						companyHotel={companyHotel}
																						isCopy={isCopy} renderForm={(companyHotel, defaultValues) =>
			<CustomPriceChangeAlert companyHotel={companyHotel} defaultValues={defaultValues} />} />;

	}

	return <></>;

};

const CustomPriceChangeAlert: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	defaultValues: AlertInput
}> = ({ companyHotel, defaultValues }) => {
	const toaster = useToaster();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const form = useForm<AlertInput>({
		defaultValues,
	});

	const [thresholdRange, setThresholdRange] = useState<Range>({
		from: -300,
		to: 300,
	});


	const queryClient = useQueryClient();
	const saveAlertMutation = useMutation({
		mutationFn: AlertService.saveAlert,
		onSuccess: (data) => {
			toaster.success('Alert', 'Alert saved successfully!');
			queryClient.setQueryData(AlertQueryKeys.detail(data.alert.id), data);
			queryClient.invalidateQueries({ queryKey: AlertQueryKeys.lists() })
				.then(() => {
					navigate('/secured/rateshopper/rates/alerts');
				});
		},
		onError: (error: string) => toaster.error('Alert', t(error)),
	});


	const onSubmit = (input: AlertInput) => {
		form.clearErrors();
		
		if (input.recipientsType === AlertRecipientsType.SELECTED && _isEmpty(input.recipientProfileIds)) {
			form.setError('recipientProfileIds', { message: 'Please select at least one recipient!' });
			return;
		}
		if (input.recipientsType === AlertRecipientsType.NONE && _isEmpty(input.extraEmails)) {
			form.setError('extraEmails', { message: 'Please add at least one extra recipient email!' });
			return;
		}
		if (_isEmpty(input.filter.roomTypes)) {
			form.setError('filter.roomTypes', { message: 'Please select at least one room type!' });
			return;
		}
		if (parseInt('' + input.priceComparisonAlertRuleInput.priceRange!.from) >= parseInt('' + input.priceComparisonAlertRuleInput.priceRange!.to)) {
			form.setError('priceComparisonAlertRuleInput.priceRange.from', { message: 'The lowest threshold should be less than the highest!' });
			form.setError('priceComparisonAlertRuleInput.priceRange.to', { message: 'The lowest threshold should be less than the highest!' });
			return;
		}
		allToEmpty(input.filter, 'roomTypes');
		if (!!input.schedule.weekdays)
			allToEmpty(input.schedule, 'weekdays');
		// @ts-ignore
		input.filter.lengthOfStay = parseInt(input.filter.lengthOfStay);
		saveAlertMutation.mutate(input);

	};

	const allToEmpty = (obj: any, arrayProperty: string) => {
		if (_get(obj, arrayProperty).includes('ALL')) _set(obj, arrayProperty, undefined);
	};

	useEffect(() => {
		if (_has(form.formState.errors, 'priceComparisonAlertRuleInput.priceRange.from')) {
			toaster.error('Price Alert', _get(form.formState.errors, 'priceComparisonAlertRuleInput.priceRange.from')!.message!);
		}
	}, [form.formState.errors]);

	const isEdit = !!defaultValues.id;

	const competitors = companyHotel.competitors
		.filter(competitor => competitor.type === CompetitorType.HOTEL)
		.map(competitor => competitor.competitors[0].hotel);

	return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
		{/* Set Price Alert */}
		<div className="mx-auto mt-8 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
			<div className="mb-6">
				<h2 className="text-base font-semibold leading-7 text-gray-900">Add Rate Comparison Alert</h2>
				<p className="text-sm leading-6 text-gray-500">
					Configure and personalize your alert in the form provided below.
				</p>
			</div>
			<div className="pt-12 border-t border-gray-200">
				<div className="space-y-12">
					{/* Section - Rulesets */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Setup</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								First, choose the competitors you want to include in the alert. Then, set the room rate that will
								trigger the alert.
							</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Competitors */}
							<div className="col-span-full">
								{!_isEmpty(competitors) &&
									<FormMultiSelect name={`priceComparisonAlertRuleInput.competitorIds`} form={form}
																	 placeholder="Select..."
																	 label="Select Competitors"
																	 isRequired
																	 hideErrorMessage
																	 disabled={isEdit}
																	 options={competitors!.map(competitor => {
																		 return {
																			 id: 'option_' + competitor.id,
																			 label: competitor.name,
																			 value: competitor.id,
																		 };
																	 })} />}
							</div>
							<div className="col-span-full">
								<FormSelect name={`priceComparisonAlertRuleInput.pricePoint`} label="Point of Reference"
														isRequired form={form} disabled={isEdit}
														size="w-full"
														optionsSize="large"
														optionsTextSize="sm"
														placeholder="Select type" options={[
									{ id: 'Median', label: 'Median', value: PriceComparisonAlertRulePricePoint.MEDIAN },
									{
										id: 'Min',
										label: 'Least Expensive',
										value: PriceComparisonAlertRulePricePoint.MIN,
									},
									{ id: 'Max', label: 'Most Expensive', value: PriceComparisonAlertRulePricePoint.MAX },
								]} />
							</div>
							<div className="sm:col-span-3">
								<FormInputNumber name={`priceComparisonAlertRuleInput.priceRange.from`}
																 label={`Lowest Threshold (%)`}
																 form={form}
																 min={thresholdRange.from}
																 max={thresholdRange.to}
																 isRequired
																 disabled={isEdit}
																 hideErrorMessage
																 placeholder="Insert value" />
							</div>
							<div className={Utilities.classNames('sm:col-span-3')}>
								<FormInputNumber name={`priceComparisonAlertRuleInput.priceRange.to`}
																 label="Highest Threshold (%)"
																 form={form}
																 min={thresholdRange.from}
																 max={thresholdRange.to}
																 disabled={isEdit}
																 isRequired
																 hideErrorMessage
																 placeholder="Insert value" />
							</div>
							<div className="col-span-full">
								<FormSelect name={`priceComparisonAlertRuleInput.trigger`}
														size="w-full"
														optionsSize="xlarge"
														optionsTextSize="sm"
														label="Trigger alert when your price is..." isRequired form={form}
														disabled={isEdit}
														placeholder="Select type" options={[
									{
										id: 'INSIDE_PRICE_RANGE',
										label: 'inside the selected threshold',
										value: PriceComparisonAlertRuleTrigger.INSIDE_PRICE_RANGE,
									},
									{
										id: 'OUTSIDE_PRICE_RANGE',
										label: 'outside of the selected threshold',
										value: PriceComparisonAlertRuleTrigger.OUTSIDE_PRICE_RANGE,
									},
								]} />

							</div>
							<div className="col-span-full">
								<AlertRuleExplanation form={form} />
							</div>
						</div>
					</div>
					{/* Section - Details */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Details</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Please configure the different details for your alert.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Room Types */}
							<div className="col-span-full">
								<AlertFilterRoomTypesField form={form} companyHotel={companyHotel} />
							</div>
							{/* Rate */}
							<div className="col-span-full">
								<AlertFilterRefundingField form={form} />
							</div>
							{/* Length of stay */}
							<div className="col-span-full">
								<AlertFilterLengthOfStayField form={form} />
							</div>
							<div className="col-span-full">
								<AlertFilterMealsField form={form} />
							</div>
						</div>
					</div>
					{/* Section - Timeframes */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Timeframe & Frequency</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Set how often the alert runs and the time period it monitors.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Frequency */}
							<div className="col-span-full">
								<AlertScheduleField form={form} defaultValues={defaultValues} />
							</div>
							<div className="col-span-full">
								<AlertTimeFrameField form={form} />
							</div>
						</div>
					</div>
					{!companyHotel.settings.alert.disableAlertProfileRecipientsSelection && <AlertRecipientsFragment form={form} />}
					<AlertAddExtraEmailsField form={form} />
					{/* Section - Push Notifications */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Finalize Alert</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">Assign a unique name to your alert. This will help you
								easily identify it
								later.</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Push Notifications */}
							{/*<div className="col-span-full">
								<AlertAddExtraEmailsField form={form} />
							</div>*/}
							{/* Alert Name */}
							<div className="col-span-full">
								<FormInputText label="Alert Name" name="name" form={form} maxLength={100}
															 placeholder="Insert alert name" isRequired />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-end my-6 space-x-4">
				<FormButton state="text" to="/secured/rateshopper/rates/alerts"
										isLoading={saveAlertMutation.isPending}>Cancel</FormButton>
				<FormButton state="primary"
										onClick={form.handleSubmit(onSubmit)}
										isLoading={saveAlertMutation.isPending}>{isEdit ? 'Save' : 'Create'} Alert</FormButton>
			</div>
		</div>
	</div>;

};

const AlertRuleExplanation: React.FunctionComponent<{ form: any }> = ({ form }) => {

	const alertRule: PriceComparisonAlertRule = form.watch('priceComparisonAlertRuleInput');

	const explainPricePoint = (pricePoint: PriceComparisonAlertRulePricePoint) => {
		switch (pricePoint) {
			case PriceComparisonAlertRulePricePoint.MIN:
				return 'the least expensive price';
			case PriceComparisonAlertRulePricePoint.MAX:
				return 'the most expensive price';
			case PriceComparisonAlertRulePricePoint.MEDIAN:
				return 'the median price';
		}
	};

	if (_isEmpty(alertRule.competitorIds)
		|| !alertRule.pricePoint
		|| !alertRule.priceRange
		|| !alertRule.priceRange.from
		|| !alertRule.priceRange.to
		|| !alertRule.trigger) return <></>;

	return <div className="w-full rounded-lg bg-white ring-1 ring-black ring-opacity-20">
		<div className="p-4">
			<div className="flex items-start">
				<div className="flex-shrink-0">
					<CheckCircleIcon className="h-6 w-6 text-green-400" />
				</div>
				<div className="ml-3 w-0 flex-1 pt-0.5">
					<p className="text-sm font-medium text-gray-900">Your alert translates
						to:</p>
					<p className="mt-1 text-sm text-gray-500">
						This alert will be triggered if your hotel price will
						be {alertRule.trigger === PriceComparisonAlertRuleTrigger.INSIDE_PRICE_RANGE ? 'inside ' : 'outside '}
						the range of {alertRule.priceRange.from}% to {alertRule.priceRange.to}%
						of {explainPricePoint(alertRule.pricePoint)} of the selected competitors
					</p>
				</div>
			</div>
		</div>
	</div>;
};

export default PriceComparisonAlertPage;